import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/ajax/queryClient";
import { Button } from "@/components/ui/button";
import { useAIFormAssistant } from "@/hooks/use-ai-form-assistant";
import { AIAssistantCard } from "@/components/ai-assistant/ai-assistant-card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  OrgFormForm,
  OrgFormFormValues,
} from "@/features/org-forms/org-form-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  FormFieldType,
  HiringProcessAssigneeRole,
  HiringProcessItemType,
} from "@api/shared-runtime/enums";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { LocalFile } from "@/components/file-upload/local-file-input";
import {
  declareUploadFiles,
  type DeclaredUploadFile,
} from "@/ajax/upload/declare-upload-file";
import { getItemTypeLabel } from "./constants";
import { RenamePromptDialog } from "./rename-prompt-dialog";
import { StageSection } from "./stage-section";
import {
  findDependentItemTitles,
  describeDependents,
} from "./dependency-utils";
import {
  type ForceReplaceNotice,
  hiringProcessTemplateFormSchema,
  type RenameChoice,
  type RenamePrompt,
} from "./schema";

export {
  hiringProcessTemplateFormSchema,
  type ExistingAttachmentsMap,
  type ExistingFileAttachment,
  type HiringProcessTemplateFormValues,
  type HiringProcessTemplateSubmitPayload,
} from "./schema";

import type {
  ExistingAttachmentsMap,
  HiringProcessTemplateFormValues,
  HiringProcessTemplateSubmitPayload,
} from "./schema";

interface HiringProcessTemplateFormProps {
  defaultValues?: Partial<HiringProcessTemplateFormValues>;
  /**
   * Already-uploaded file attachments from an existing template (edit mode).
   * Keyed by `${stageIndex}-${itemIndex}`.
   */
  existingAttachments?: ExistingAttachmentsMap;
  onSubmit: (payload: HiringProcessTemplateSubmitPayload) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  error?: string | null;
}

export function HiringProcessTemplateForm({
  defaultValues,
  existingAttachments,
  onSubmit,
  onCancel,
  isSubmitting,
  error,
}: HiringProcessTemplateFormProps) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const buId = selectedOrgBusinessUnit.id;
  const enableDocumentSigning =
    selectedOrgBusinessUnit.enableDocumentSigning === true;

  /** Pending local files not yet uploaded, keyed by `${stageIndex}-${itemIndex}` */
  const localFilesRef = useRef<Map<string, LocalFile[]>>(new Map());

  /**
   * Original names/titles from the loaded template, keyed by DB id. Built once
   * at mount; used at submit time to detect renames so we can prompt the user.
   */
  const originalStageNames = useState(() => {
    const m = new Map<string, string>();
    defaultValues?.stages?.forEach((stage) => {
      if (stage.id) {
        m.set(stage.id, stage.name);
      }
    });
    return m;
  })[0];
  const originalItemTitles = useState(() => {
    const m = new Map<string, string>();
    defaultValues?.stages?.forEach((stage) => {
      stage.items?.forEach((item) => {
        if (item.id) {
          m.set(item.id, item.title);
        }
      });
    });
    return m;
  })[0];
  /** Original itemType + assigneeRole per item id — used to detect force-replace changes. */
  const originalItemDefs = useState(() => {
    const m = new Map<
      string,
      {
        itemType: HiringProcessItemType;
        assigneeRole: HiringProcessAssigneeRole;
      }
    >();
    defaultValues?.stages?.forEach((stage) => {
      stage.items?.forEach((item) => {
        if (item.id) {
          m.set(item.id, {
            itemType: item.itemType,
            assigneeRole: item.assigneeRole,
          });
        }
      });
    });
    return m;
  })[0];

  /** IDs the user has explicitly removed (via trash button or "recreate" choice). */
  const removedStageIdsRef = useRef<Set<string>>(new Set());
  const removedItemIdsRef = useRef<Set<string>>(new Set());

  /** Rename / replace prompt dialog state. When set, blocks submission until resolved. */
  const [renamePromptState, setRenamePromptState] = useState<{
    prompts: RenamePrompt[];
    notices: ForceReplaceNotice[];
    onResolve: (choices: Record<string, RenameChoice>) => void;
    onCancel: () => void;
  } | null>(null);

  const { data: availableForms } = useQuery(
    trpc.organizationForm.listForms.queryOptions({
      organizationBusinessUnitId: buId,
    }),
  );

  const form = useForm<HiringProcessTemplateFormValues>({
    resolver: zodResolver(hiringProcessTemplateFormSchema),
    defaultValues: {
      name: "",
      description: "",
      isDefault: false,
      stages: [
        {
          name: "Stage 1",
          description: "",
          sortOrder: 0,
          items: [],
        },
      ],
      ...defaultValues,
    },
  });

  const [validationError, setValidationError] = useState<string | null>(null);
  /** Item keys (`${stageIdx}-${itemIdx}`) that failed the doc-share file check */
  const [docShareErrorKeys, setDocShareErrorKeys] = useState<Set<string>>(
    new Set(),
  );
  const validationBannerRef = useRef<HTMLDivElement>(null);

  /** Inline form creation/editing dialog state */
  const [formDialogTarget, setFormDialogTarget] = useState<{
    stageIndex: number;
    itemIndex: number;
    /** When set, the dialog opens pre-populated for editing a newFormSpec */
    editingSpec?: OrgFormFormValues;
  } | null>(null);

  const createFormMutation = useMutation(
    trpc.organizationForm.createForm.mutationOptions(),
  );

  const handleCreateForm = (stageIndex: number, itemIndex: number) => {
    setFormDialogTarget({ stageIndex, itemIndex });
  };

  const handleEditFormSpec = (stageIndex: number, itemIndex: number) => {
    const spec = form.getValues(
      `stages.${stageIndex}.items.${itemIndex}.newFormSpec`,
    );
    if (!spec) {
      return;
    }
    setFormDialogTarget({
      stageIndex,
      itemIndex,
      editingSpec: {
        name: spec.name,
        description: spec.description ?? "",
        fields: spec.fields.map((f) => ({
          label: f.label,
          description: f.description ?? "",
          fieldType: f.fieldType as FormFieldType,
          sortOrder: f.sortOrder,
          isRequired: f.isRequired,
          options: f.options ?? null,
        })),
      },
    });
  };

  const handleFormDialogSubmit = async (values: OrgFormFormValues) => {
    if (!formDialogTarget) {
      return;
    }
    const { stageIndex, itemIndex, editingSpec } = formDialogTarget;

    if (editingSpec) {
      // Editing an AI-proposed spec — update the newFormSpec in form state
      form.setValue(`stages.${stageIndex}.items.${itemIndex}.newFormSpec`, {
        name: values.name,
        description: values.description ?? null,
        fields: values.fields.map((f) => ({
          label: f.label,
          description: f.description ?? null,
          fieldType: f.fieldType,
          sortOrder: f.sortOrder,
          isRequired: f.isRequired,
          options: f.options ?? null,
        })),
      });
      setFormDialogTarget(null);
      return;
    }

    // Creating a new form — persist to DB and link
    try {
      const result = await createFormMutation.mutateAsync({
        organizationBusinessUnitId: buId,
        name: values.name,
        description: values.description ?? null,
        fields: values.fields.map((f) => ({
          label: f.label,
          description: f.description ?? null,
          fieldType: f.fieldType,
          sortOrder: f.sortOrder,
          isRequired: f.isRequired,
          options: f.options ?? null,
        })),
      });

      form.setValue(
        `stages.${stageIndex}.items.${itemIndex}.formId`,
        result.id,
      );

      await queryClient.invalidateQueries(
        trpc.organizationForm.listForms.queryOptions({
          organizationBusinessUnitId: buId,
        }),
      );

      setFormDialogTarget(null);
    } catch (err) {
      // Error is shown via createFormMutation.error
    }
  };

  const transformAIAssistFormState = React.useCallback(
    (
      nextState: HiringProcessTemplateFormValues,
      currentState: HiringProcessTemplateFormValues,
    ): HiringProcessTemplateFormValues => {
      const usedStageIds = new Set<string>();
      const nextStageIds = new Set<string>();
      const currentStages = currentState.stages ?? [];
      const currentStageById = new Map(
        currentStages
          .filter((stage) => stage.id)
          .map((stage) => [stage.id!, stage]),
      );
      const currentStageByName = new Map(
        currentStages.map((stage) => [stage.name, stage]),
      );

      const stages = nextState.stages.map((stage, stageIdx) => {
        const existingById = stage.id
          ? currentStageById.get(stage.id)
          : undefined;
        const existingByName = currentStageByName.get(stage.name);
        const existingByIndex = currentStages[stageIdx];
        const existing =
          existingById ??
          (existingByName?.id && !usedStageIds.has(existingByName.id)
            ? existingByName
            : undefined) ??
          (existingByIndex?.id && !usedStageIds.has(existingByIndex.id)
            ? existingByIndex
            : undefined);

        if (!existing?.id) {
          return stage;
        }

        usedStageIds.add(existing.id);
        nextStageIds.add(existing.id);

        const usedItemIds = new Set<string>();
        const nextItemIds = new Set<string>();
        const currentItems = existing.items ?? [];
        const currentItemById = new Map(
          currentItems
            .filter((item) => item.id)
            .map((item) => [item.id!, item]),
        );
        const currentItemByRef = new Map(
          currentItems
            .filter((item) => item.ref)
            .map((item) => [item.ref!, item]),
        );
        const currentItemByTitle = new Map(
          currentItems.map((item) => [item.title, item]),
        );

        const items = stage.items.map((item, itemIdx) => {
          const existingItemById = item.id
            ? currentItemById.get(item.id)
            : undefined;
          const existingItemByRef = item.ref
            ? currentItemByRef.get(item.ref)
            : undefined;
          const existingItemByTitle = currentItemByTitle.get(item.title);
          const existingItemByIndex = currentItems[itemIdx];
          const existingItem =
            existingItemById ??
            (existingItemByRef?.id && !usedItemIds.has(existingItemByRef.id)
              ? existingItemByRef
              : undefined) ??
            (existingItemByTitle?.id && !usedItemIds.has(existingItemByTitle.id)
              ? existingItemByTitle
              : undefined) ??
            (existingItemByIndex?.id && !usedItemIds.has(existingItemByIndex.id)
              ? existingItemByIndex
              : undefined);

          if (!existingItem?.id) {
            return item;
          }

          usedItemIds.add(existingItem.id);
          nextItemIds.add(existingItem.id);
          return {
            ...item,
            id: existingItem.id,
            ref: item.ref ?? existingItem.ref ?? existingItem.id,
          };
        });

        for (const item of currentItems) {
          if (item.id && !nextItemIds.has(item.id)) {
            removedItemIdsRef.current.add(item.id);
          }
        }

        return {
          ...stage,
          id: existing.id,
          items,
        };
      });

      for (const stage of currentStages) {
        if (stage.id && !nextStageIds.has(stage.id)) {
          removedStageIdsRef.current.add(stage.id);
        }
      }

      return {
        ...nextState,
        stages,
      };
    },
    [],
  );

  const aiAssistant = useAIFormAssistant({
    formType: "hiringProcessTemplate",
    form,
    transformFormState: transformAIAssistFormState,
  });

  const aiStarterPrompts = [
    "Standard hiring process: screening questions, initial interview, background check, and credentialing verification",
    "New hire onboarding: policy acknowledgments, document signing, and first-day checklist",
  ];

  const {
    fields: stageFields,
    append: appendStage,
    remove: removeStage,
    move: moveStage,
  } = useFieldArray({
    control: form.control,
    name: "stages",
  });

  const handleAddStage = () => {
    appendStage({
      name: `Stage ${stageFields.length + 1}`,
      description: "",
      sortOrder: stageFields.length,
      items: [],
    });
  };

  const handleRemoveStage = (stageIndex: number) => {
    if (stageFields.length === 1) {
      return;
    }
    const stages = form.getValues("stages");
    // Block removal while steps in OTHER stages depend on any step in this
    // stage. The user must clear those dependencies first, otherwise the save
    // references steps that no longer exist.
    const removedRefs = new Set(
      (stages[stageIndex]?.items ?? [])
        .map((i) => i.ref)
        .filter((ref): ref is string => !!ref),
    );
    const dependents = findDependentItemTitles(stages, removedRefs);
    if (dependents.length > 0) {
      toast.error(
        `Can't remove this stage — ${describeDependents(dependents)} depend${
          dependents.length === 1 ? "s" : ""
        } on its steps. Remove those dependencies first.`,
      );
      return;
    }
    const stage = form.getValues(`stages.${stageIndex}`);
    if (stage.id) {
      removedStageIdsRef.current.add(stage.id);
      // Any items inside that stage are soft-deleted server-side when the
      // stage is, so we don't need to individually record their ids.
    }
    removeStage(stageIndex);
    const updatedStages = form.getValues("stages");
    updatedStages.forEach((_, i) => {
      form.setValue(`stages.${i}.sortOrder`, i);
    });
  };

  /** Called from StageSection when an existing item is removed. */
  const handleRecordRemovedItemId = (itemId: string) => {
    removedItemIdsRef.current.add(itemId);
  };

  /**
   * Wait for the user to resolve a rename / force-replace dialog; returns
   * their per-id rename choice (notices are informational only).
   */
  const openRenamePrompt = (
    prompts: RenamePrompt[],
    notices: ForceReplaceNotice[],
  ): Promise<Record<string, RenameChoice> | null> => {
    return new Promise((resolve) => {
      setRenamePromptState({
        prompts,
        notices,
        onResolve: (choices) => {
          setRenamePromptState(null);
          resolve(choices);
        },
        onCancel: () => {
          setRenamePromptState(null);
          resolve(null);
        },
      });
    });
  };

  const handleMoveStageUp = (stageIndex: number) => {
    if (stageIndex === 0) {
      return;
    }
    moveStage(stageIndex, stageIndex - 1);
    const updatedStages = form.getValues("stages");
    updatedStages.forEach((_, i) => {
      form.setValue(`stages.${i}.sortOrder`, i);
    });
  };

  const handleMoveStageDown = (stageIndex: number) => {
    if (stageIndex === stageFields.length - 1) {
      return;
    }
    moveStage(stageIndex, stageIndex + 1);
    const updatedStages = form.getValues("stages");
    updatedStages.forEach((_, i) => {
      form.setValue(`stages.${i}.sortOrder`, i);
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setValidationError(null);
    setDocShareErrorKeys(new Set());

    // 1. Run Zod field-level validation
    const zodValid = await form.trigger();

    // 2. Run custom cross-field / external-state validation
    //    RHF's nested useFieldArray can drop empty `items` arrays from
    //    getValues() when an inner field array has unregistered (the
    //    default shouldUnregister behavior on unmount). Normalize once
    //    here so the schema's `items: array` invariant holds below.
    const values = form.getValues();
    for (const stage of values.stages) {
      if (!stage.items) {
        stage.items = [];
      }
    }
    const customErrors: string[] = [];
    const errorKeys = new Set<string>();

    values.stages.forEach((stage, si) => {
      stage.items.forEach((item, ii) => {
        const itemKey = `${si}-${ii}`;
        const label = item.title || `Item ${ii + 1}`;

        // Form items require a linked form or a pending form spec
        if (
          item.itemType === HiringProcessItemType.Form &&
          item.formId == null &&
          !item.newFormSpec
        ) {
          errorKeys.add(itemKey);
          customErrors.push(
            `Stage ${si + 1}, "${label}": a linked form is required`,
          );
        }

        // DocumentShare items require at least one shared file or link.
        if (item.itemType === HiringProcessItemType.DocumentShare) {
          const keptFileUploadIds = new Set(item.fileUploadIds ?? []);
          const existing = (existingAttachments?.get(itemKey) ?? []).filter(
            (file) => keptFileUploadIds.has(file.fileUploadId),
          );
          const hasExisting = existing.length > 0;
          const hasNew = (localFilesRef.current.get(itemKey)?.length ?? 0) > 0;
          const hasLink = (item.links ?? []).some((link) => link.url.trim());
          if (!hasExisting && !hasNew && !hasLink) {
            errorKeys.add(itemKey);
            customErrors.push(
              `Stage ${si + 1}, "${label}": a shared file or link is required`,
            );
          }
          if (existing.some((file) => file.scanStatus === "quarantined")) {
            errorKeys.add(itemKey);
            customErrors.push(
              `Stage ${si + 1}, "${label}": remove the file rejected by the security scan`,
            );
          }
        }

        if (
          item.itemType === HiringProcessItemType.DocumentSigning &&
          !enableDocumentSigning
        ) {
          errorKeys.add(itemKey);
          customErrors.push(
            `Stage ${si + 1}, "${label}": document signing is not enabled for this business unit`,
          );
        }
      });
    });

    // 3. Build combined error message from Zod + custom errors
    const zodErrors: string[] = [];
    if (!zodValid) {
      const errors = form.formState.errors;
      if (errors.name) {
        zodErrors.push("Template name is required");
      }
      if (errors.stages?.root) {
        zodErrors.push(errors.stages.root.message ?? "");
      }
      const stageErrors = errors.stages;
      if (stageErrors && Array.isArray(stageErrors)) {
        stageErrors.forEach(
          (stageErr: Record<string, unknown> | undefined, si: number) => {
            if (!stageErr) {
              return;
            }
            if (stageErr.name) {
              zodErrors.push(`Stage ${si + 1}: name is required`);
            }
            const items = stageErr.items;
            if (items && Array.isArray(items)) {
              items.forEach(
                (itemErr: Record<string, unknown> | undefined, ii: number) => {
                  if (!itemErr) {
                    return;
                  }
                  if (itemErr.title) {
                    zodErrors.push(
                      `Stage ${si + 1}, Item ${ii + 1}: title is required`,
                    );
                  }
                },
              );
            }
          },
        );
      }
    }

    const allErrors = [...zodErrors, ...customErrors];
    if (allErrors.length > 0) {
      setDocShareErrorKeys(errorKeys);
      setValidationError(allErrors.join(". "));
      setTimeout(
        () =>
          validationBannerRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          }),
        0,
      );
      return;
    }

    // 4. Create any pending forms from AI-proposed newFormSpec
    const resolvedFormIds = new Map<string, string>();
    for (const [si, stage] of values.stages.entries()) {
      for (const [ii, item] of stage.items.entries()) {
        if (item.newFormSpec && !item.formId) {
          try {
            const created = await createFormMutation.mutateAsync({
              organizationBusinessUnitId: buId,
              name: item.newFormSpec.name,
              description: item.newFormSpec.description ?? null,
              fields: item.newFormSpec.fields.map((f) => ({
                label: f.label,
                description: f.description ?? null,
                fieldType: f.fieldType as FormFieldType,
                sortOrder: f.sortOrder,
                isRequired: f.isRequired,
                options: f.options ?? null,
              })),
            });
            resolvedFormIds.set(`${si}-${ii}`, created.id);
          } catch (err) {
            setValidationError(
              `Failed to create form "${item.newFormSpec.name}": ${err instanceof Error ? err.message : "Unknown error"}`,
            );
            return;
          }
        }
      }
    }

    // 5. Detect renames — any stage/item whose id is known and whose name
    //    differs from its originally-loaded name. And detect force-replace
    //    situations: items whose itemType or assigneeRole changed (backend
    //    soft-deletes the old row and creates a new one regardless).
    const prompts: RenamePrompt[] = [];
    const notices: ForceReplaceNotice[] = [];
    for (const stage of values.stages) {
      if (stage.id) {
        const orig = originalStageNames.get(stage.id);
        if (orig !== undefined && orig !== stage.name) {
          prompts.push({
            kind: "stage",
            id: stage.id,
            oldName: orig,
            newName: stage.name,
          });
        }
      }
      for (const item of stage.items) {
        if (item.id) {
          const origTitle = originalItemTitles.get(item.id);
          if (origTitle !== undefined && origTitle !== item.title) {
            prompts.push({
              kind: "item",
              id: item.id,
              stageName: stage.name,
              oldName: origTitle,
              newName: item.title,
            });
          }
          const origDef = originalItemDefs.get(item.id);
          if (origDef) {
            if (origDef.itemType !== item.itemType) {
              notices.push({
                id: item.id,
                stageName: stage.name,
                itemName: item.title,
                reason: `Type changed from ${getItemTypeLabel(origDef.itemType)} to ${getItemTypeLabel(item.itemType)}`,
              });
            } else if (origDef.assigneeRole !== item.assigneeRole) {
              notices.push({
                id: item.id,
                stageName: stage.name,
                itemName: item.title,
                reason: `Assignee changed from ${origDef.assigneeRole === HiringProcessAssigneeRole.Provider ? "Provider" : "Organization"} to ${item.assigneeRole === HiringProcessAssigneeRole.Provider ? "Provider" : "Organization"}`,
              });
            }
          }
        }
      }
    }

    let choices: Record<string, RenameChoice> = {};
    if (prompts.length > 0 || notices.length > 0) {
      const result = await openRenamePrompt(prompts, notices);
      if (result === null) {
        return;
      } // user cancelled
      choices = result;
    }

    // Build the final removedIds lists: explicit removals + any "recreate" choices.
    const removedStageIds = new Set(removedStageIdsRef.current);
    const removedItemIds = new Set(removedItemIdsRef.current);
    for (const prompt of prompts) {
      if (choices[prompt.id] === "recreate") {
        if (prompt.kind === "stage") {
          removedStageIds.add(prompt.id);
        } else {
          removedItemIds.add(prompt.id);
        }
      }
    }

    // Final safety net: every surviving step's ref. Used to drop predecessors
    // pointing at steps that are no longer in the payload, so a save can never
    // reference a removed step. The UI blocks removing a depended-on step, but
    // AI edits / "recreate" choices can still orphan a ref.
    const validRefs = new Set(
      values.stages.flatMap((stage) =>
        stage.items
          .map((item) => item.ref)
          .filter((ref): ref is string => !!ref),
      ),
    );

    // Each new file's reservation carries its hash, computed before the
    // payload is built (the mapping below is synchronous).
    const newFilesByItemKey = new Map<string, DeclaredUploadFile[]>();
    for (const [itemKey, itemLocalFiles] of localFilesRef.current.entries()) {
      newFilesByItemKey.set(
        itemKey,
        await declareUploadFiles(itemLocalFiles.map((lf) => lf.file)),
      );
    }

    // 6. Normalize and submit. Stage/item ids are kept on rename, stripped on recreate.
    const normalized = {
      ...values,
      stages: values.stages.map((stage, stageIdx) => {
        const stageRecreate =
          stage.id !== undefined && choices[stage.id] === "recreate";
        return {
          ...stage,
          id: stageRecreate ? undefined : stage.id,
          sortOrder: stageIdx,
          items: stage.items.map((item, itemIdx) => {
            const itemKey = `${stageIdx}-${itemIdx}`;
            const itemRecreate =
              item.id !== undefined && choices[item.id] === "recreate";
            return {
              ...item,
              id: itemRecreate ? undefined : item.id,
              formId: resolvedFormIds.get(itemKey) ?? item.formId ?? null,
              newFormSpec: undefined,
              sortOrder: itemIdx,
              // Drop predecessors whose target step is no longer present.
              predecessors: (item.predecessors ?? []).filter((p) =>
                validRefs.has(p.ref),
              ),
              newFiles: newFilesByItemKey.get(itemKey) ?? [],
            };
          }),
        };
      }),
    };

    onSubmit({
      values: normalized,
      localFiles: getLocalFilesMap(),
      removedStageIds: Array.from(removedStageIds),
      removedItemIds: Array.from(removedItemIds),
    });
  };

  /** Returns a snapshot of the local files keyed by `${stageIdx}-${itemIdx}` */
  const getLocalFilesMap = (): Map<string, File[]> => {
    const result = new Map<string, File[]>();
    for (const [key, localFiles] of localFilesRef.current.entries()) {
      if (localFiles.length > 0) {
        result.set(
          key,
          localFiles.map((lf) => lf.file),
        );
      }
    }
    return result;
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSave} className="space-y-6 px-4 sm:px-6">
        {/* AI Assistant — front and center */}
        <AIAssistantCard
          messages={aiAssistant.messages}
          onSendMessage={aiAssistant.sendMessage}
          isLoading={aiAssistant.isLoading}
          onClearConversation={aiAssistant.clearConversation}
          title="Build your hiring process with AI"
          emptyStatePrompt="Describe the hiring process you want and I'll create the stages and tasks."
          inputPlaceholder="e.g. Describe the hiring process you want..."
          starterPrompts={aiStarterPrompts}
          pendingAttachments={aiAssistant.pendingAttachments}
          onAddAttachment={aiAssistant.addAttachment}
          onRemoveAttachment={aiAssistant.removeAttachment}
          hasInvalidAttachment={aiAssistant.hasInvalidAttachment}
        />

        {/* Template metadata */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="col-span-2 sm:col-span-1">
                  <FormLabel>Template Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Standard Onboarding Process"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="col-span-2 sm:col-span-1 flex items-end gap-3 pb-0.5">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div>
                    <FormLabel className="mb-0">Default Template</FormLabel>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Auto-attach to new job posts
                    </p>
                  </div>
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description (optional)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe what this hiring process is for..."
                    rows={2}
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Stages */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-base font-semibold">Stages</Label>
            <span className="text-sm text-gray-500">
              {stageFields.length} stage{stageFields.length !== 1 ? "s" : ""}
            </span>
          </div>

          {form.formState.errors.stages?.root && (
            <p className="text-sm text-red-600 mb-3">
              {form.formState.errors.stages.root.message}
            </p>
          )}

          <div className="space-y-3">
            {stageFields.map((stageField, stageIndex) => (
              <StageSection
                key={stageField.id}
                stageIndex={stageIndex}
                totalStages={stageFields.length}
                form={form}
                availableForms={availableForms ?? []}
                onRemoveStage={handleRemoveStage}
                onRecordRemovedItemId={handleRecordRemovedItemId}
                onMoveStageUp={handleMoveStageUp}
                onMoveStageDown={handleMoveStageDown}
                localFilesRef={localFilesRef}
                existingAttachments={existingAttachments}
                docShareErrorKeys={docShareErrorKeys}
                enableDocumentSigning={enableDocumentSigning}
                onCreateForm={handleCreateForm}
                onEditFormSpec={handleEditFormSpec}
              />
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-4 w-full border-dashed border-blue-300 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            onClick={handleAddStage}
          >
            <Plus size={14} className="mr-1.5" />
            Add Stage
          </Button>
        </div>

        {validationError && (
          <div
            ref={validationBannerRef}
            className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800"
          >
            {validationError}
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2 pb-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save Template"}
          </Button>
        </div>
      </form>

      {/* Rename-vs-recreate confirmation dialog */}
      {renamePromptState && (
        <RenamePromptDialog
          prompts={renamePromptState.prompts}
          notices={renamePromptState.notices}
          onConfirm={renamePromptState.onResolve}
          onCancel={renamePromptState.onCancel}
        />
      )}

      {/* Inline form creation / edit dialog */}
      <Dialog
        open={!!formDialogTarget}
        onOpenChange={(open) => {
          if (!open) {
            setFormDialogTarget(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] sm:max-h-[85vh] overflow-y-auto !p-0">
          <DialogHeader className="mb-4 pt-6 px-6">
            <DialogTitle>
              {formDialogTarget?.editingSpec ? "Edit Form" : "Create Form"}
            </DialogTitle>
          </DialogHeader>
          {formDialogTarget && (
            <OrgFormForm
              key={`${formDialogTarget.stageIndex}-${formDialogTarget.itemIndex}-${formDialogTarget.editingSpec ? "edit" : "create"}`}
              defaultValues={formDialogTarget.editingSpec}
              onSubmit={handleFormDialogSubmit}
              onCancel={() => setFormDialogTarget(null)}
              isSubmitting={createFormMutation.isPending}
              error={createFormMutation.error?.message}
            />
          )}
        </DialogContent>
      </Dialog>
    </Form>
  );
}
